import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import {
  HashIcon,
  LightbulbIcon,
  NetworkIcon,
  SearchIcon,
  TargetIcon,
  UsersIcon,
} from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import type { UserMeOwnerOrganisation } from "simplydo/interfaces/types/userTypes";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { List } from "simplydo/react/ui/list";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useCurrentUser, userCache } from "utils/currentUser";
import { useContainerDimensions } from "utils/useContainerDimensions";
import util from "utils/utils";

import { StyledSearchItem } from "./SearchItem";
import UserSearchItem from "./UserSearchItem";
import { type SpotlightResult, getPath } from "./locations";
import type {
  OmniHoveredResult,
  OmniSearchChallenge,
  OmniSearchIdea,
  OmniSearchOrganisation,
} from "./types";

export const StyledSearchBar = styled.div`
  margin-right: ${({ theme }) => (theme.sizes.isMobile ? 0 : 20)}px;
  width: 100%;
`;

export const NoSearchContainer = styled.div<{ $width: number }>`
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: fadeIn 0.3s ease-in-out forwards;
  width: 170px;
  width: ${({ theme, $width }) =>
    theme.sizes.isMobile ? "calc(100vw - 30px)" : `calc(${$width}px - 30px)`};
  min-width: ${({ theme }) => (theme.sizes.isMobile ? "0" : "600px")};
  overflow-wrap: break-word;
  overflow: hidden;
`;

export const StyledSearchResults = styled.div<{ $width: number }>`
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: fadeIn 0.3s ease-in-out forwards;
  max-height: 760px;
  overflow: auto;
  width: ${({ theme, $width }) =>
    theme.sizes.isMobile ? "calc(100vw - 30px)" : `calc(${$width}px - 30px)`};
  min-width: ${({ theme }) => (theme.sizes.isMobile ? "0" : "600px")};

  h4 {
    margin-bottom: 5px;
  }
  .ui.list {
    margin-top: 0;
  }
`;

export const StyledTitleRow = styled.div<{ $spaceTop?: boolean }>`
  display: flex;
  flex-direction: row;
  margin: 0 !important;
  margin-top: ${({ $spaceTop }) => ($spaceTop ? "10px" : "0")} !important;
`;

export const StyledTitle = styled.h4`
  margin: 0;
`;

export const StyledSpotlightResult = styled(Link)<{ $active?: boolean }>`
  background-color: ${({ $active }) => ($active ? "#ced3d9" : "#e9ebee")};
  padding: 10px;
  width: 100%;
  display: flex;
  flex-direction: row;
  border-radius: 5px;
`;

type SearchResults = OpenAPI.GET<"/search/omni">["response"];
type RecentSearches =
  OpenAPI.GET<"/search/omni/recent">["response"]["recentSearches"];
const searchResultsAreEmpty = (searchResults: Partial<SearchResults>) => {
  return (
    util.isEmpty(searchResults) ||
    Object.values(searchResults).every((val) => val.length === 0)
  );
};

type OmniBarProps = { onClose?: () => void };

const OmniBar = ({ onClose }: OmniBarProps) => {
  const user = useCurrentUser();
  const onSwitchOrganisation = useCallback((org: UserMeOwnerOrganisation) => {
    userCache.switchOrganisation(org);
  }, []);

  const theme = useTheme();
  const searchInput = useRef<HTMLInputElement | null>(null);
  const searchWrapperRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Partial<SearchResults>>(
    {},
  );
  const [searching, setSearching] = useState(false);
  const [hovered, setHovered] = useState<OmniHoveredResult | null>(null);
  const [spotlightResults, setSpotlightResults] = useState<SpotlightResult[]>(
    [],
  );
  const [recentSearches, setRecentSearches] = useState<RecentSearches>([]);
  const [cursor, setCursor] = useState(0);
  const { width } = useContainerDimensions(
    searchWrapperRef as React.RefObject<HTMLDivElement>,
  );

  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    users = [],
    challenges = [],
    ideas = [],
    groups = [],
    organisations = [],
    tags = [],
  } = searchResults;
  const totalResults = useMemo(
    () =>
      searchTerm
        ? [
            ...spotlightResults,
            ...users,
            ...challenges,
            ...ideas,
            ...groups,
            ...(organisations || []),
            ...tags,
          ]
        : recentSearches,
    [
      searchTerm,
      spotlightResults,
      users,
      challenges,
      ideas,
      groups,
      organisations,
      tags,
      recentSearches,
    ],
  );

  const userStartIndex = spotlightResults.length;
  const challengeStartIndex =
    userStartIndex + (searchResults.users?.length ?? 0);
  const ideaStartIndex =
    challengeStartIndex + (searchResults.challenges?.length ?? 0);
  const groupStartIndex = ideaStartIndex + (searchResults.ideas?.length ?? 0);
  const organisationStartIndex =
    groupStartIndex + (searchResults.groups?.length ?? 0);
  const tagStartIndex =
    organisationStartIndex + (searchResults.organisations?.length ?? 0);

  useEffect(() => {
    api.search.getRecentSearches(
      ({ recentSearches: newRecentSearches }) =>
        setRecentSearches(newRecentSearches),
      () => {},
    );
  }, []);

  const addRecentSearch = useCallback((type: string, name: string) => {
    api.search.recentSearch(
      { type, search: name },
      (data) => {
        setRecentSearches(data.recentSearches);
      },
      () => {},
    );
  }, []);

  const switchOrganisation = useCallback(
    (_e: React.MouseEvent, org: Pick<OmniSearchOrganisation, "_id">) => {
      if (!user) return;
      api.users.switchOrganisation(
        user._id,
        org._id,
        (data) => {
          onSwitchOrganisation(data.organisation);
        },
        (err) => toast.error(err.message),
      );
    },
    [user, onSwitchOrganisation],
  );

  const finishSearch = useCallback(() => {
    setOpen(false);
    setSearchTerm("");
    setSearchResults({});
    setSpotlightResults([]);
    setCursor(0);
    setSearching(false);
    setHovered(null);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function downHandler({ key }: ReactKeyboardEvent | KeyboardEvent) {
      switch (key) {
        case "ArrowUp": {
          const nextCursor =
            cursor === 0 ? totalResults.length - 1 : cursor - 1;
          setCursor(nextCursor);
          setHovered(totalResults[nextCursor] as OmniHoveredResult);
          break;
        }
        case "ArrowDown": {
          const nextCursor =
            cursor === totalResults.length - 1 ? 0 : cursor + 1;
          setCursor(nextCursor);
          setHovered(totalResults[nextCursor] as OmniHoveredResult);
          break;
        }
        case "Enter": {
          const isSpotlight = cursor < userStartIndex;
          const isUser =
            cursor >= userStartIndex && cursor < challengeStartIndex;
          const isChallenge =
            cursor >= challengeStartIndex && cursor < ideaStartIndex;
          const isIdea = cursor >= ideaStartIndex && cursor < groupStartIndex;
          const isGroup =
            cursor >= groupStartIndex && cursor < organisationStartIndex;
          const isOrganisation = cursor >= organisationStartIndex;
          const isTag = cursor >= tagStartIndex;
          if (hovered) {
            if (isSpotlight) {
              navigate(hovered.link ?? "/");
            } else if (isUser) {
              addRecentSearch("user", hovered.profile?.fullName ?? "");
              navigate(`/users/${hovered._id}`);
            } else if (isChallenge) {
              addRecentSearch("challenge", hovered.name ?? "");
              navigate(`/challenges/${hovered._id}`);
            } else if (isIdea) {
              addRecentSearch("idea", hovered.name ?? "");
              navigate(`/ideas/${hovered._id}`);
            } else if (isGroup) {
              addRecentSearch("group", hovered.name ?? "");
              navigate(`/groups/${hovered._id}`);
            } else if (isOrganisation) {
              addRecentSearch("organisation", hovered.name ?? "");
              switchOrganisation(
                {} as ReactMouseEvent,
                hovered as OmniSearchOrganisation,
              );
            } else if (isTag) {
              navigate(`/tags/${hovered._id}`);
            }
          }
          break;
        }
      }
    }

    window.addEventListener("keydown", downHandler);
    return () => {
      window.removeEventListener("keydown", downHandler);
    };
  }, [
    challengeStartIndex,
    cursor,
    groupStartIndex,
    hovered,
    ideaStartIndex,
    navigate,
    open,
    organisationStartIndex,
    switchOrganisation,
    tagStartIndex,
    totalResults,
    userStartIndex,
    addRecentSearch,
  ]);

  const search = useThrottle(
    (term) => {
      if (!term) return;
      setSearching(true);
      api.search.omni(
        { query: term },
        (results) => {
          setOpen(true);
          setSearching(false);
          setSearchResults(results);
        },
        () => setSearching(false),
      );
    },
    400,
    [],
  );

  const updateSearchTerm = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const term = e.target.value;
      setSearchTerm(e.target.value);
      if (term) {
        if (term.length >= 2) {
          setSearching(true);
          search(e.target.value);
        }
        setSpotlightResults(getPath(user, term));
      } else {
        setSpotlightResults([]);
        setSearchResults({});
        setSearching(false);
      }
    },
    [search, user],
  );

  const userIsAdmin = util.hasPermission(
    user,
    "org.viewDashboard",
    user?.ownerOrganisation?._id,
  );
  const showVectorOmniScores =
    util.hasPermission(user, "super.viewDashboard") &&
    (
      user?.ownerOrganisation?.enabledFeatures as string[] | undefined
    )?.includes("vectorOmniSearch");
  const omniScoreStyle = {
    opacity: 0.5,
    fontSize: "0.75rem",
    flexShrink: 0,
  } as const;
  const hasSearchResults = useMemo(
    () => !searchResultsAreEmpty(searchResults),
    [searchResults],
  );

  const hoverControls = (index: number) => ({
    onMouseEnter: () => {
      if (index >= totalResults.length) {
        setCursor(0);
        setHovered(null);
        return;
      }
      setHovered(totalResults[index] as OmniHoveredResult);
      setCursor(index);
    },
  });

  return (
    <StyledSearchBar>
      <Popup
        fluid
        on="focus"
        position="bottom center"
        open={open}
        offset={[0, theme.sizes.isMobile ? -10 : 6]}
        onClose={() => {
          setOpen(false);
          onClose?.();
        }}
        onOpen={() => {
          api.journey.record("users", null, "clickedOmniBar");
          setOpen(true);
          if (searchInput.current) {
            searchInput.current?.focus?.();
          }
        }}
        style={{
          maxHeight: 800,
          width: theme.sizes.isMobile ? "100vw" : "fit-content",
        }}
        trigger={
          <div ref={searchWrapperRef}>
            <Input
              icon={<Icon icon={SearchIcon} />}
              fluid
              autoFocus={theme.sizes.isMobile}
              value={searchTerm}
              iconPosition="left"
              placeholder={
                !userIsAdmin
                  ? `Search ${util.appName()}...`
                  : t("omni.search.placeholder")
              }
              onChange={updateSearchTerm}
              loading={searching}
              ref={searchInput}
            />
          </div>
        }
      >
        {searchTerm ? (
          !hasSearchResults && !spotlightResults.length && !searching ? (
            <NoSearchContainer $width={width}>
              <H4 style={{ marginBottom: 10 }}>
                No results found
                <Subheader>
                  Try adjusting your search query or check your spelling.
                </Subheader>
              </H4>
            </NoSearchContainer>
          ) : (
            <StyledSearchResults $width={width}>
              <Grid mobile={1} columns={2}>
                <GridColumn span={2}>
                  {spotlightResults.length ? (
                    <StyledTitleRow $spaceTop>
                      <StyledTitle>Pages</StyledTitle>
                    </StyledTitleRow>
                  ) : null}
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 5 }}
                  >
                    {spotlightResults.map((result, i) => (
                      <StyledSpotlightResult
                        key={result.title}
                        $active={cursor === i}
                        to={result.link}
                        onClick={finishSearch}
                        {...hoverControls(i)}
                      >
                        <Icon icon={result.icon} style={{ marginRight: 5 }} />
                        <p>
                          <b>{result.title}</b> - {result.link}
                        </p>
                      </StyledSpotlightResult>
                    ))}
                  </div>
                </GridColumn>
                {searchResults.users && searchResults.users.length > 0 ? (
                  <GridColumn style={{ marginTop: 0 }}>
                    <H4>People</H4>
                    {searchResults.users.map((searchUser, index) => (
                      <UserSearchItem
                        active={index + userStartIndex === cursor}
                        key={searchUser._id}
                        user={searchUser}
                        onClick={() => {
                          addRecentSearch("user", searchUser.profile.fullName);
                          finishSearch();
                        }}
                        {...hoverControls(index + userStartIndex)}
                      />
                    ))}
                  </GridColumn>
                ) : null}

                {searchResults.challenges &&
                searchResults.challenges.length > 0 ? (
                  <GridColumn style={{ marginTop: 0 }}>
                    <H4>
                      {t("common:capitalise", { key: "generic.challenges" })}
                    </H4>
                    <List>
                      {searchResults.challenges.map((challenge, index) => {
                        const searchChallenge =
                          challenge as OmniSearchChallenge;
                        return (
                          <StyledSearchItem
                            $active={index + challengeStartIndex === cursor}
                            key={searchChallenge._id}
                            onClick={() => {
                              addRecentSearch(
                                "challenge",
                                searchChallenge.name,
                              );
                              finishSearch();
                            }}
                            {...hoverControls(index + challengeStartIndex)}
                            asChild
                          >
                            <Link to={`/challenges/${searchChallenge._id}`}>
                              <Icon icon={TargetIcon} />
                              <span>{searchChallenge.name}</span>
                              {showVectorOmniScores &&
                              searchChallenge.search?.score != null ? (
                                <span style={omniScoreStyle}>
                                  {Number(searchChallenge.search.score).toFixed(
                                    4,
                                  )}
                                </span>
                              ) : null}
                            </Link>
                          </StyledSearchItem>
                        );
                      })}
                    </List>
                  </GridColumn>
                ) : null}

                {searchResults.ideas && searchResults.ideas.length > 0 ? (
                  <GridColumn>
                    <H4>{t("common:capitalise", { key: "generic.ideas" })}</H4>
                    <List>
                      {searchResults.ideas.map((idea, index) => {
                        const searchIdea = idea as OmniSearchIdea;
                        return (
                          <StyledSearchItem
                            $active={index + ideaStartIndex === cursor}
                            key={searchIdea._id}
                            onClick={() => {
                              addRecentSearch("idea", searchIdea.name ?? "");
                              finishSearch();
                            }}
                            {...hoverControls(index + ideaStartIndex)}
                            asChild
                          >
                            <Link to={`/ideas/${searchIdea._id}`}>
                              <Icon icon={LightbulbIcon} />
                              <span>{searchIdea.name}</span>
                              {showVectorOmniScores &&
                              searchIdea.search?.score != null ? (
                                <span style={omniScoreStyle}>
                                  {Number(searchIdea.search.score).toFixed(4)}
                                </span>
                              ) : null}
                            </Link>
                          </StyledSearchItem>
                        );
                      })}
                    </List>
                  </GridColumn>
                ) : null}

                {searchResults.groups && searchResults.groups.length > 0 ? (
                  <GridColumn>
                    <H4>{t("common:capitalise", { key: "generic.groups" })}</H4>
                    <List>
                      {searchResults.groups.map((group, index) => (
                        <StyledSearchItem
                          $active={index + groupStartIndex === cursor}
                          key={group._id}
                          onClick={() => {
                            addRecentSearch("group", group.name ?? "");
                            finishSearch();
                          }}
                          {...hoverControls(index + groupStartIndex)}
                          asChild
                        >
                          <Link to={`/groups/${group._id}`}>
                            <Icon icon={UsersIcon} />
                            <span>{group.name}</span>
                          </Link>
                        </StyledSearchItem>
                      ))}
                    </List>
                  </GridColumn>
                ) : null}

                {searchResults.organisations &&
                searchResults.organisations.length > 0 ? (
                  <GridColumn>
                    <H4>Organisations</H4>
                    <List>
                      {searchResults.organisations.map((org, index) => (
                        <StyledSearchItem
                          $active={index + organisationStartIndex === cursor}
                          key={org._id}
                          onClick={(e: ReactMouseEvent) => {
                            addRecentSearch("organisation", org.name ?? "");
                            switchOrganisation(e, org);
                            finishSearch();
                          }}
                          {...hoverControls(index + organisationStartIndex)}
                          asChild
                        >
                          <Link to={"#"}>
                            <Icon icon={NetworkIcon} />
                            <span>{org.name}</span>
                          </Link>
                        </StyledSearchItem>
                      ))}
                    </List>
                  </GridColumn>
                ) : null}
                {searchResults.tags && searchResults.tags.length > 0 ? (
                  <GridColumn>
                    <H4>Tags</H4>
                    <List>
                      {searchResults.tags.map((tag, index) => (
                        <StyledSearchItem
                          $active={index + tagStartIndex === cursor}
                          key={tag._id}
                          onClick={() => {
                            addRecentSearch("tag", tag.value);
                            finishSearch();
                          }}
                          {...hoverControls(index + tagStartIndex)}
                          asChild
                        >
                          <Link to={`/tags/${tag._id}`}>
                            <Icon icon={HashIcon} />
                            <span>{tag.value}</span>
                          </Link>
                        </StyledSearchItem>
                      ))}
                    </List>
                  </GridColumn>
                ) : null}
              </Grid>
            </StyledSearchResults>
          )
        ) : (
          <NoSearchContainer $width={width}>
            {recentSearches && recentSearches.length > 0 ? (
              <>
                <H4 style={{ marginBottom: 10 }}>Recent searches</H4>
                {recentSearches.slice(0, 5).map((sr, index) => (
                  <StyledSearchItem
                    key={index}
                    onClick={() => {
                      setSpotlightResults([]);
                      setSearchResults({});
                      updateSearchTerm({
                        target: { value: sr.search },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    $active={cursor === index}
                    {...hoverControls(index)}
                  >
                    <Icon icon={SearchIcon} />
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>{sr.search}</span>
                      <span
                        style={{
                          color: "rgba(0,0,0,0.3)",
                          textTransform: "capitalize",
                        }}
                      >
                        {sr.type}
                      </span>
                    </div>
                  </StyledSearchItem>
                ))}
              </>
            ) : (
              <>
                <H4 style={{ marginBottom: 10 }}>
                  {t("omni.search.title")}
                  <Subheader>{t("omni.search.info")}</Subheader>
                </H4>
              </>
            )}
          </NoSearchContainer>
        )}
      </Popup>
    </StyledSearchBar>
  );
};

export default OmniBar;
